import React, { useEffect, useState } from 'react'
import { Button, Group, Stack } from '@mantine/core'
import MainTable from 'app/views/components/Table/MainTable'
import { loadTradeNoteUnreadCounts } from 'app/state/ducks/trade-notes/thunks'
import { columnDefs } from './trade-enty-page-columnDefs'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { getEntities } from 'app/state/ducks/entities/selectors'
import TradeEntryPanel from './trade-entry-panel'
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput'
import { tradeViewColumns } from 'app/models/dropdown-options'
import { sortData } from 'app/utils/util-functions'
import { transformTradeDataForExport } from './utils/trade-data-export-util'
import {
    GridPaginationStorage,
    GridColumnVisibilityStorage,
    useGridPagination,
    useGridColumnVisibility,
    loadTradeSortFromStorage,
    saveTradeSortToStorage,
} from 'app/utils/grid-sorting-storage'
import { IconFolders } from '@tabler/icons-react'
import GenerateDownloadModal from 'app/views/components/modals/generate-download-modal'
import { generateMasterTradeReport, generateSubTradeReport } from 'app/state/ducks/trade-reports/thunks'
import { masterTradeStatusLabel } from './utils/master-trade-status'
import { useHasPermission, EXPORT_TRADES_PERMISSION } from 'app/hooks/useHasPermission'
import { FilterValues } from './trade-entry'
import { TradeListRow } from './utils/trade-list-rows'

interface ExpandableMantineTableProps {
    /** Already filtered and built by the page from GET /api/trade-list. */
    rows: TradeListRow[]
    globalLoading: boolean
    title: string
    filterValues?: FilterValues
}

/**
 * The Trade List table.
 *
 * Rows arrive joined and folded by the server (`GET /api/trade-list`) and
 * shaped by `buildTradeListRows`; this component sorts, exports and renders.
 * It no longer reads deals, entities, facilities, traded facilities or sub
 * trades to stitch each row — the only stores touched here are the unread
 * counts for the Stream column and, when the report modal opens, deals and
 * entities to label the active filters in the report header.
 */
const ExpandableMantineTable: React.FC<ExpandableMantineTableProps> = ({
    rows,
    globalLoading,
    title,
    filterValues,
}) => {
    const dispatch = useDispatch()
    const [, setIsInactiveLoan] = React.useState(false)
    const allDeals = useSelector(getDeals)
    const allEntities = useSelector(getEntities)
    const [tradeReportModalOpen, setTradeReportModalOpen] = useState(false)
    const canExportTrades = useHasPermission(EXPORT_TRADES_PERMISSION)

    const resolveFiltersForReport = (): Record<string, string> => {
        const resolved: Record<string, string> = {}
        if (!filterValues) return resolved

        const formatDateRange = (value: string | Date[] | null[]) => {
            if (!Array.isArray(value)) return ''
            const [start, end] = value
            if (!start) return ''
            const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
            return end ? `${fmt(new Date(start))} - ${fmt(new Date(end))}` : fmt(new Date(start))
        }

        for (const [key, value] of Object.entries(filterValues)) {
            if (!value) continue

            switch (key) {
                case 'tradeStatus':
                    // Tolerates a non-array here: filters restored from storage predate
                    // this filter being multi-value.
                    resolved['Trade Status'] = (Array.isArray(value) ? value : [value as string])
                        .map(masterTradeStatusLabel)
                        .join(', ')
                    break
                case 'tradeDate': {
                    const formatted = formatDateRange(value as Date[])
                    if (formatted) resolved['Trade Date'] = formatted
                    break
                }
                case 'settlementDate': {
                    const formatted = formatDateRange(value as Date[])
                    if (formatted) resolved['Settlement Date'] = formatted
                    break
                }
                case 'tradeAction':
                    resolved['Trade Action'] = value as string
                    break
                case 'deal':
                    resolved['Deal'] = allDeals.find((d) => d.id === value)?.dealName ?? (value as string)
                    break
                case 'entityAnySide':
                    resolved['Entity (Any Side)'] = allEntities.find((e) => e.id === value)?.entityName ?? (value as string)
                    break
                case 'myEntity':
                    resolved['My Entity'] = allEntities.find((e) => e.id === value)?.entityName ?? (value as string)
                    break
                case 'counterparty':
                    resolved['Counterparty'] = allEntities.find((e) => e.id === value)?.entityName ?? (value as string)
                    break
                case 'agent':
                    resolved['Agent'] = allEntities.find((e) => e.id === value)?.entityName ?? (value as string)
                    break
                case 'paymentMethod':
                    resolved['Payment Method'] = value as string
                    break
                case 'aaStatus':
                    resolved['AA Status'] = value as string
                    break
                case 'unappliedTransactions':
                    resolved['Unapplied Transactions'] = value as string
                    break
                case 'lockStatus':
                    resolved['Lock Status'] = value as string
                    break
                case 'holdStatus':
                    resolved['Hold Status'] = value as string
                    break
                case 'allocated':
                    resolved['Allocated'] = value as string
                    break
                case 'activityStream':
                    resolved['Activity Stream'] = value as string
                    break
                case 'staleDocuments':
                    resolved['Stale Documents'] = value as string
                    break
                case 'documentsNotRefreshed':
                    resolved['Documents Not Refreshed'] = value as string
                    break
            }
        }

        return resolved
    }

    const handleTradeReportDownload = async (selections: Record<string, string>) => {
        const { reportType } = selections as {
            fileFormat: 'xls' | 'csv'
            reportType: 'master' | 'subtrade' | 'fullExtract'
        }

        if (reportType !== 'master' && reportType !== 'subtrade') {
            throw new Error(`Report type "${reportType}" is not yet supported`)
        }

        const tradeIds = sortedRows
            .map((trade) => trade.id)
            .filter(Boolean) as string[]

        if (tradeIds.length === 0) {
            throw new Error('No trade data available to export')
        }

        const filters = resolveFiltersForReport()
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const now = new Date()
        const executedTimestamp = now.toLocaleDateString('en-CA').replace(/-/g, '/') + ' ' +
            now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase().replace(' ', '') + ' ' +
            new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(now).find(p => p.type === 'timeZoneName')?.value

        const result = reportType === 'subtrade'
            ? await generateSubTradeReport(tradeIds, filters, executedTimestamp, timezone)
            : await generateMasterTradeReport(tradeIds, filters, executedTimestamp, timezone)
        if (!result.success) {
            throw new Error(result.payload || 'Failed to generate report')
        }

        return true
    }

    // Load sort preferences from localStorage
    const initialSort = loadTradeSortFromStorage()
    const [sortBy, setSortBy] = useState<string>(initialSort.sortBy)
    const [order, setOrder] = useState<string>(initialSort.order)

    // Grid preferences hooks (only pagination and column visibility, no sorting since it's disabled)
    const { pagination, handlePaginationChange } = useGridPagination(
        GridPaginationStorage.tradeEntry,
    )
    const { columnVisibility, handleColumnVisibilityChange } =
        useGridColumnVisibility(GridColumnVisibilityStorage.tradeEntry)

    const handleToggle = (isActive: boolean) => {
        setIsInactiveLoan(isActive)
    }

    // Save sort preferences when they change
    useEffect(() => {
        saveTradeSortToStorage({
            sortBy,
            order: order as 'Ascending' | 'Descending',
        })
    }, [sortBy, order])

    useEffect(() => {
        // Stream-column unread badges (read-state design) — manual refresh like every read.
        dispatch(loadTradeNoteUnreadCounts())
    }, [])

    // The report header names the active filters' deals and entities. Those
    // stores are not loaded for the list any more, so fetch them when the
    // modal opens; both thunks are coalesced.
    useEffect(() => {
        if (!tradeReportModalOpen) return
        dispatch(loadAllDeals())
        dispatch(loadEntities())
    }, [tradeReportModalOpen, dispatch])

    const renderDetailPanel = (row: {
        original: TradeListRow
        getIsExpanded: () => boolean
    }) => <TradeEntryPanel row={row.original} isExpanded={row.getIsExpanded()} />

    // The Sort By dropdown compares the stored field, so ordering on Status would
    // follow the ledger value while the column shows the label (LOS-5334) — putting
    // Closed between Open and Submitted. Sort a projection carrying the label, then
    // map back to the untouched rows.
    const sortedRows = sortBy === 'status'
        ? sortData(
            rows.map((trade, index) => ({
                index,
                status: masterTradeStatusLabel(trade.status),
            })),
            'status',
            order as 'Ascending' | 'Descending',
        ).map(({ index }) => rows[index])
        : sortData(
            rows,
            sortBy,
            order as 'Ascending' | 'Descending',
        )

    // transform the trade data so that it can be exported properly
    const tradeExportData = transformTradeDataForExport(sortedRows)

    return (
        <>
            <Stack w="100%" className="sectionRemoveGap sectionBody" style={{ padding: '10px 20px 20px 20px', backgroundColor: '#F5EEEE', border: '1px solid #B4B3B3' }}>
                <Group className="tradeTableHeader">
                    <div className="tradeTotalAndReportGroup">
                        <div className="tradeTotalGroup">
                            <IconFolders />
                            <span style={{ color: '#784595' }}>Total Trades: </span>
                            <span>{sortedRows.length}</span>
                        </div>
                        {canExportTrades && (
                            <Button
                                style={{ width: '120px', height: '35px', borderRadius: '5px', backgroundColor: '#32439A', color: '#fff' }}
                                onClick={() => setTradeReportModalOpen(true)}
                            >
                                Trade Report
                            </Button>
                        )}
                    </div>
                    <Group
                        className="tradeSortGroup"
                        style={{ justifyContent: 'flex-end', marginRight: '22px' }}
                    >
                        <NewSelectInput
                            wrapperType="trade"
                            label="Sort By"
                            data={tradeViewColumns}
                            w="164px"
                            h="42px"
                            size='18px'
                            placeholder="Sort By"
                            value={sortBy}
                            onChange={(value) => setSortBy(value ?? 'tradeDate')}
                        />
                        <NewSelectInput
                            wrapperType="trade"
                            label="Order"
                            data={['Ascending', 'Descending']}
                            w="164px"
                            h="42px"
                            placeholder="Order"
                            value={order}
                            onChange={(value) => setOrder(value ?? 'Ascending')}
                        />
                    </Group>
                </Group>
                <Stack
                    w="100%"
                    pt="12px"
                    mt="10px"
                    className="sectionDetailBody"
                    style={{ padding: '0px' }}
                >
                    {columnDefs.length != 0 ? (
                        <MainTable
                            tableName={title}
                            columnDefs={columnDefs}
                            withPagination={true}
                            data={sortedRows ?? []}
                            initialPagination={pagination}
                            onPaginationChange={handlePaginationChange}
                            initialColumnVisibility={columnVisibility}
                            onColumnVisibilityChange={handleColumnVisibilityChange}
                            renderDetailPanel={({ row }) => renderDetailPanel(row)}
                            enableTopToolbar={false}
                            headerBackgroundColor="#F0EEEE"
                            disableStriping={true}
                            headerVerticalAlign="middle"
                            isLoading={globalLoading}
                            isAccordionTable={true}
                            showBottomBoolean={false}
                            onToggle={(isActive) => handleToggle(isActive)}
                            border={true}
                            fromTradeEntry={true}
                            enableColumnOrdering={false}
                            enableColumnResizing={false}
                            enableGrouping={false}
                            enableColumnFilters={false}
                            enableColumnActions={false}
                            enableFilters={false}
                            enableSorting={false}
                            enableFullScreenToggle={false}
                            enableDensityToggle={false}
                            enableHiding={false}
                            customExportData={tradeExportData}
                            highlightRow={({ original }) => original.holds?.some((h: any) => !h.liftedAt) ? '#FCEEEE' : undefined}
                            setRow={() => { }}
                        />
                    ) : null}
                </Stack>
            </Stack >
            <GenerateDownloadModal
                opened={tradeReportModalOpen}
                onClose={() => setTradeReportModalOpen(false)}
                onDownload={handleTradeReportDownload}
            />
        </>
    )
}

export default ExpandableMantineTable
